import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy | Tablecure";
  }, []);

  const lastUpdated = "April 12, 2025";

  return (
    <div className="policy-page tc-page">
      <div className="pd2-bg">
        <div className="policy-orb policy-orb1" />
        <div className="policy-orb policy-orb2" />
        <div className="ck-grid" />
      </div>

      <div className="policy-inner">
        <div className="policy-header">
          <div className="pd2-eyebrow">
            <span className="pd2-eyebrow-dot" />
            Legal
          </div>
          <h1 className="policy-title">Privacy Policy</h1>
          <p className="policy-subtitle">
            Last updated: {lastUpdated}
          </p>
        </div>

        <div className="policy-body">

          <section className="policy-section">
            <h2>1. Introduction</h2>
            <p>
              Tablecure ("we", "our", or "us") is committed to protecting your personal information
              and your right to privacy. This Privacy Policy explains how we collect, use, disclose,
              and safeguard your information when you visit <strong>tablecure.com</strong> and make
              purchases from our online store.
            </p>
            <p>
              Please read this policy carefully. If you disagree with its terms, please discontinue
              use of our site.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. Information We Collect</h2>
            <h3>Information you provide directly</h3>
            <ul>
              <li><strong>Account information:</strong> name, email address, and password when you register.</li>
              <li><strong>Order information:</strong> shipping address, billing address, phone number, and payment details when you place an order.</li>
              <li><strong>Communications:</strong> messages you send us via email or the contact form.</li>
            </ul>
            <h3>Information collected automatically</h3>
            <ul>
              <li><strong>Usage data:</strong> pages visited, time spent on pages, links clicked, and referring URLs.</li>
              <li><strong>Device data:</strong> browser type, operating system, IP address, and device identifiers.</li>
              <li><strong>Cookies:</strong> small data files stored on your device (see Section 7).</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Process and fulfil your orders, including payment processing and delivery.</li>
              <li>Send order confirmations, shipping updates, and customer service communications.</li>
              <li>Create and manage your account.</li>
              <li>Improve our website, products, and services.</li>
              <li>Detect, prevent, and address fraud or security issues.</li>
              <li>Comply with applicable legal obligations.</li>
              <li>Send you promotional communications if you have opted in (you may opt out at any time).</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>4. Sharing Your Information</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties for their marketing purposes.
              We may share your information with:
            </p>
            <ul>
              <li><strong>Payment processors:</strong> Razorpay processes payments on our behalf. Your payment card details are never stored on our servers.</li>
              <li><strong>Shipping partners:</strong> courier and logistics companies receive your shipping address and order details to deliver your purchase.</li>
              <li><strong>Analytics providers:</strong> Google Analytics 4 helps us understand site usage in aggregate form.</li>
              <li><strong>Legal authorities:</strong> when required by law, court order, or to protect our legal rights.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>5. Data Retention</h2>
            <p>
              We retain your personal data for as long as necessary to fulfil the purposes described in this policy,
              including to satisfy legal, accounting, or reporting requirements. Order records are retained for a
              minimum of 7 years as required by Indian tax law. You may request deletion of your account at any
              time by contacting us.
            </p>
          </section>

          <section className="policy-section">
            <h2>6. Data Security</h2>
            <p>
              We implement appropriate technical and organisational security measures to protect your personal
              information against accidental or unlawful destruction, loss, alteration, or unauthorised access.
              These measures include HTTPS encryption for all data in transit and hashed storage of passwords.
            </p>
            <p>
              No method of transmission over the internet or method of electronic storage is 100% secure.
              While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="policy-section">
            <h2>7. Cookies</h2>
            <p>We use the following types of cookies:</p>
            <ul>
              <li><strong>Strictly necessary cookies:</strong> required for the site to function (e.g., shopping cart state, authentication token).</li>
              <li><strong>Analytics cookies:</strong> Google Analytics 4 uses cookies to collect aggregated, anonymised usage statistics.</li>
              <li><strong>Preference cookies:</strong> remember your theme preference (light / dark mode).</li>
            </ul>
            <p>
              You can control cookies through your browser settings. Disabling strictly necessary cookies may
              prevent some features from working correctly.
            </p>
          </section>

          <section className="policy-section">
            <h2>8. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy
              practices of those sites and encourage you to review their privacy policies before providing
              any personal information.
            </p>
          </section>

          <section className="policy-section">
            <h2>9. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul>
              <li>Access a copy of the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your data ("right to be forgotten").</li>
              <li>Object to or restrict processing of your data.</li>
              <li>Data portability — receive your data in a structured, machine-readable format.</li>
              <li>Withdraw consent where processing is based on consent.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:support@tablecure.com">support@tablecure.com</a>.
              We will respond within 30 days.
            </p>
          </section>

          <section className="policy-section">
            <h2>10. Children's Privacy</h2>
            <p>
              Our website is not directed to children under 13 years of age. We do not knowingly collect
              personal information from children under 13. If you believe we have inadvertently collected
              such information, please contact us immediately.
            </p>
          </section>

          <section className="policy-section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes
              by posting the new policy on this page with a revised "Last updated" date. Continued use of
              the site after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="policy-section">
            <h2>12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or your personal data, please reach out:
            </p>
            <div className="policy-contact-block">
              <p><strong>Tablecure</strong></p>
              <p>Email: <a href="mailto:support@tablecure.com">support@tablecure.com</a></p>
              <p>Website: <Link to="/contact">Contact Form</Link></p>
            </div>
          </section>

        </div>

        <div className="policy-footer-nav">
          <Link to="/" className="pd2-btn-ghost">← Back to Home</Link>
          <div className="policy-footer-links">
            <Link to="/terms" className="policy-footer-link">Terms of Service</Link>
            <span className="policy-footer-sep" />
            <Link to="/shipping-policy" className="policy-footer-link">Shipping &amp; Returns</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
